<script lang="ts">
  // Catalogue view — three modes per INTERFACE-SPECIFICATION.md §§4–6.
  //
  //   /catalogue                      → Matrix (default)
  //   /catalogue/matrix               → Matrix
  //   /catalogue/comparison           → Comparison
  //   /catalogue/detail/{jur}/{prod}  → Detail (read-only here;
  //                                     editing lives in commit 5)

  import { onMount } from 'svelte';
  import { route, navigate } from '../lib/router.svelte';
  import { bootstrapManifest, fetchCatalogue, catalogueState } from '../lib/catalogue-store.svelte';
  import { PRODUCTS, productKey } from '../lib/products';
  import type { Jurisdiction, ProductId } from '../lib/types';
  import CatalogueMatrix from '../components/CatalogueMatrix.svelte';
  import CatalogueComparison from '../components/CatalogueComparison.svelte';
  import CatalogueDetail from '../components/CatalogueDetail.svelte';
  import SurfaceViewToolbar from '../components/SurfaceViewToolbar.svelte';

  onMount(async () => {
    // Re-fetch on every mount of the Catalogue route so the proposer
    // never sees a stale cube — a previously-cached in-memory entry
    // would otherwise persist across navigations even after a merge
    // updated KV. Manifest refresh first (cheap) so any new in_effect
    // version metadata is current too; catalogue store ignores cache
    // for unhashed live reads.
    await bootstrapManifest();
    await Promise.all(
      PRODUCTS.map((p) => fetchCatalogue(p.jurisdiction, p.product))
    );
  });

  type MatrixSubTab = 'rates' | 'fees' | 'purposes' | 'terms' | 'methodology';
  const MATRIX_SUBTABS: readonly MatrixSubTab[] = ['rates', 'fees', 'purposes', 'terms', 'methodology'];
  const DEFAULT_SUBTAB: MatrixSubTab = 'rates';

  const mode = $derived(route.current.params.mode ?? 'matrix');
  const rawSubtab = $derived(route.current.params.subtab as MatrixSubTab | undefined);
  const matrixSubtab = $derived<MatrixSubTab>(
    rawSubtab && MATRIX_SUBTABS.includes(rawSubtab) ? rawSubtab : DEFAULT_SUBTAB
  );
  const detailJurisdiction = $derived((route.current.params.jurisdiction as Jurisdiction | undefined) ?? 'au');
  const detailProduct = $derived((route.current.params.product as ProductId | undefined) ?? 'unsecured-personal-loan');

  // Per INTERFACE-REVIEW.md §5 R6, the Matrix sub-tabs live in the URL
  // so they are deep-linkable, the browser back button walks them, and
  // a screenshot's address bar is enough to reproduce the view.
  // Canonicalise the two bare entry paths to their default sub-tab so
  // a bookmark of `/catalogue` or `/catalogue/matrix` lands on a fully-
  // qualified URL the user can copy and share without ambiguity.
  $effect(() => {
    if (route.current.name !== 'catalogue') return;
    if (route.current.path === '/catalogue') {
      navigate(`/catalogue/matrix/${DEFAULT_SUBTAB}`, { replace: true });
      return;
    }
    if (mode === 'matrix' && !rawSubtab) {
      navigate(`/catalogue/matrix/${DEFAULT_SUBTAB}`, { replace: true });
    }
  });

  let leftKey = $state<string>(productKey({ jurisdiction: 'au', product: 'unsecured-personal-loan' }));
  let rightKey = $state<string>(productKey({ jurisdiction: 'nz', product: 'unsecured-personal-loan' }));

  function onComparisonPick(side: 'left' | 'right', key: string) {
    if (side === 'left') leftKey = key;
    else rightKey = key;
  }

  function goTo(target: 'matrix' | 'comparison' | 'detail') {
    if (target === 'detail') {
      navigate(`/catalogue/detail/${detailJurisdiction}/${detailProduct}`);
    } else if (target === 'matrix') {
      navigate(`/catalogue/matrix/${matrixSubtab}`);
    } else {
      navigate(`/catalogue/${target}`);
    }
  }
</script>

<section>
  <nav class="mode-tabs" aria-label="Catalogue mode">
    <button type="button" class:is-active={mode === 'matrix'} onclick={() => goTo('matrix')}>Matrix</button>
    <button type="button" class:is-active={mode === 'comparison'} onclick={() => goTo('comparison')}>Comparison</button>
    <button type="button" class:is-active={mode === 'detail'} onclick={() => goTo('detail')}>Detail</button>
  </nav>

  {#if mode === 'matrix' || mode === 'comparison'}
    <SurfaceViewToolbar showView={mode === 'comparison' || (mode === 'matrix' && matrixSubtab === 'rates')} />
  {/if}

  {#if mode === 'detail'}
    <!-- Product picker for Detail mode. Without this, the user lands
         on /catalogue/detail/{default} via the mode-tabs Detail button
         (or a fresh navigation to the route) and has no in-page way
         to switch to the other three products. The nav renders all
         four products as URL-anchored links so each one is one click
         away, deep-linkable, and right-click-shareable — same pattern
         the Matrix sub-tabs use (INTERFACE-REVIEW.md §5 R6). -->
    <nav class="product-nav" aria-label="Detail product picker">
      {#each PRODUCTS as p (productKey(p))}
        <a
          href={`/catalogue/detail/${p.jurisdiction}/${p.product}`}
          class:is-active={p.jurisdiction === detailJurisdiction && p.product === detailProduct}
        >{p.label}</a>
      {/each}
    </nav>
  {/if}

  {#if mode === 'matrix'}
    <CatalogueMatrix subTab={matrixSubtab} />
  {:else if mode === 'comparison'}
    {#key leftKey + '|' + rightKey}
      <CatalogueComparison {leftKey} {rightKey} onPick={onComparisonPick} />
    {/key}
  {:else if mode === 'detail'}
    {#key detailJurisdiction + '/' + detailProduct}
      <CatalogueDetail jurisdiction={detailJurisdiction} product={detailProduct} />
    {/key}
  {:else}
    <p class="muted">Unknown mode: {mode}</p>
  {/if}
</section>

<style>
  .mode-tabs {
    display: flex;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
  }
  .mode-tabs button {
    border: 1px solid var(--surface-line);
    background: var(--surface-card);
    padding: var(--space-1) var(--space-3);
    font-size: var(--type-sm);
    cursor: pointer;
    color: var(--surface-muted);
    border-radius: var(--radius-sm);
  }
  .mode-tabs button.is-active {
    background: var(--surface-fg);
    color: var(--action-primary-fg);
    border-color: var(--surface-fg);
  }
  .product-nav {
    display: flex;
    gap: var(--space-2);
    margin-bottom: var(--space-4);
    flex-wrap: wrap;
  }
  .product-nav a {
    border: 1px solid var(--surface-line);
    background: var(--surface-card);
    padding: var(--space-1) var(--space-3);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    border-radius: var(--radius-sm);
    text-decoration: none;
  }
  .product-nav a:hover {
    color: var(--surface-fg);
    border-color: var(--surface-fg);
  }
  .product-nav a.is-active {
    background: var(--surface-fg);
    color: var(--action-primary-fg);
    border-color: var(--surface-fg);
  }
</style>
